import React, { Component } from 'react';
// @ts-ignore
import CurrencyInput from 'react-currency-input';
import { Icon } from 'semantic-ui-react';
import Config from '../lib/Config';

/**
 * @param {{
 *  outline?: boolean,
 *  disabled?: boolean,
 *  selected?: boolean,
 * }} param0
 */
const checkboxIconClassName = ({ outline, disabled, selected }) => {
  if (selected) {
    return `icon square check${outline ? ' outline' : ' selected'}`;
  }
  return `icon square unselected${disabled ? ' disabled' : ''}`;
};

/**
 * @typedef {{
 *  type?: string,
 *  onChange?: (val: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement> | number | string) => void,
 *  value: string | number,
 *  onClear?: () => void,
 *  placeholder?: string,
 *  error?: any,
 *  style?: React.CSSProperties,
 *  className?: string,
 *  icon?: string,
 *  iconBefore?: string,
 *  iconAfter?: string,
 *  selected?: boolean,
 *  label?: string | JSX.Element,
 *  onClick?: () => void,
 *  description?: string,
 *  children?: React.ReactNode,
 *  id?: string,
 *  disabled?: boolean,
 *  maxPercentage?: number,
 *  currency?: 'BRL' | 'USD',
 *  checkboxProps?: {
 *    noBackground?: boolean,
 *    outline?: boolean,
 *    style?: Object,
 *    right?: boolean,
 *    left?: boolean
 *  },
 *  onKeyPress?: () => void,
 *  inputType?: string
 * }} Props
 * @extends Component<Props>
 */
export default class Input extends Component {
  /** @param {Props} props */
  renderInput = (props) => {
    const {
      icon, iconBefore, iconAfter, onClear, selected, error, label, description, children,
      checkboxProps, ...inputProps
    } = props;
    const {
      value, type = 'text', id, placeholder, onChange, disabled, onKeyPress
    } = inputProps;

    if (type === 'search') {
      return (
        <div className="search-input">
          <Icon className="search" />

          <input
            placeholder="Busca"
            {...inputProps}
            type="text"
          />

          {value && <Icon className="times circle" onClick={onClear} />}
        </div>
      );
    }

    if (type === 'percentage' || type === 'interest') {
      return (
        <div className={`text-input ${type}`}>
          <input
            placeholder="0"
            {...inputProps}
            type="number"
            onKeyPress={(e) => {
              if (e.key === 'E' || e.key === 'e' || e.key === '-') e.preventDefault();
            }}
            onChange={(e) => {
              const newValue = +e.target.value;
              const maxPercentage = inputProps.maxPercentage ? inputProps.maxPercentage : 100;
              if (newValue > maxPercentage || newValue < 0) {
                e.preventDefault();
                return;
              }
              if (onChange) onChange(newValue);
            }}
          />
          {children}

          {type === 'interest'
            ? <Icon className="percentage interest"><p style={{ fontWeight: 'normal', fontSize: '15px' }}>&nbsp;ao mês</p></Icon>
            : <Icon className="percentage align-right" />
          }
        </div>
      );
    }

    if (type === 'radio') {
      return (
        <label
          htmlFor={id}
          id={id}
          className={`radio-input ${selected ? 'selected' : ''} ${disabled ? 'disabled' : ''}`}
        >
          {placeholder || value}
          <input {...inputProps} />
          {children}
          {selected ? <Icon className="scrubber selected" /> : <Icon className="circle unselected" />}
        </label>
      );
    }

    if (type === 'product') {
      return (
        <label
          htmlFor={id}
          id={id}
          className={`radio-input ${selected ? 'selected' : ''} ${disabled ? 'disabled' : ''}`}
        >
          {placeholder || value}
          <input {...inputProps} />
          {children}
          {selected ? <Icon className="circle check selected" /> : <Icon className="circle unselected" />}
        </label>
      );
    }

    // TODO Use accessible-checkbox type as checkbox
    if (type === 'accessible-checkbox') {
      const { onClick, ...otherProps } = inputProps;
      return (
        <div
          className={`checkbox-input${selected ? ' selected' : ''
            }${disabled ? ' disabled' : ''
            }${checkboxProps?.noBackground ? ' no-background' : ''
            }`}
        >
          <input {...otherProps} type="checkbox" id={`${id || value}`} onChange={onClick} defaultChecked={selected} />

          <i
            className={checkboxIconClassName({ ...(checkboxProps || {}), selected })}
            style={checkboxProps?.style || {}}
            data-testid={`display-square${selected ? 'Checked' : 'Unchecked'}-input`}
          />

          <label htmlFor={`${id || value}`}>
            {placeholder || value}
            {children}
          </label>
        </div>
      );
    }

    if (type === 'checkbox') {
      return (
        <label
          htmlFor={id}
          id={id}
          className={`checkbox-input ${selected ? 'selected' : ''} ${disabled ? 'disabled' : ''} ${checkboxProps?.noBackground ? 'no-background' : ''}`}
        >
          {placeholder || value}
          <input {...inputProps} />
          {children}
          {selected
            ? <Icon className={`square check ${checkboxProps?.outline ? 'outline' : 'selected'}`} style={checkboxProps?.style ? { ...checkboxProps.style } : {}} />
            : <Icon className={`square unselected${disabled ? ' disabled' : ''}`} style={checkboxProps?.style ? { ...checkboxProps.style } : {}} />}
        </label>
      );
    }

    if (type === 'organization-radio') {
      return (
        <label
          htmlFor={id}
          id={id}
          className={`radio-input big ${selected ? 'selected' : ''} ${disabled ? 'disabled' : ''}`}
        >
          {placeholder || value}
          <input {...inputProps} />
          {children}
          {selected ? <Icon className="circle check selected align-right" /> : <Icon className="circle unselected align-right" />}
        </label>
      );
    }

    if (type === 'big-radio' || type === 'big-checkbox') {
      return (
        <label
          htmlFor={id}
          id={id}
          className={`${type.slice(4)}-input big ${selected ? 'selected' : ''
            } ${disabled ? 'disabled' : ''
            } ${checkboxProps?.right ? 'right' : ''
            } ${checkboxProps?.left ? 'left' : ''
            }`}
        >
          {icon && (
            <div className="icon-border">
              <Icon className={icon} />
            </div>
          )}
          <div className="content">
            <p className="label">{label}</p>
            <p className="description">{description}</p>
          </div>
          <input {...inputProps} type={type === 'big-radio' ? 'radio' : 'checkbox'} />
          {children}
          {selected
            ? <Icon className={`${type === 'big-radio' ? 'scrubber' : 'square check'} selected`} />
            : <Icon className={`${type === 'big-radio' ? 'circle' : 'square'} unselected`} />}
        </label>
      );
    }

    if (type === 'currency') {
      const { currency = 'BRL' } = this.props;

      return (
        <CurrencyInput
          id="currencyInput"
          className="currency-input"
          decimalSeparator={Config.currency[currency].decimalSeparator}
          thousandSeparator={Config.currency[currency].thousandSeparator}
          prefix={`${Config.currency[currency].label} `}
          precision={2}
          value={value}
          onPaste={/** @param {ClipboardEvent} e */(e) => {
            const newValue = `${(+value).toFixed(2).replace(/\D/g, '')}${e.clipboardData?.getData('Text').replace(/\D/g, '')}`;
            if (onChange) onChange(+((+(newValue) / 100).toFixed(2)));
          }}
          onChangeEvent={/** @param {React.ChangeEvent<HTMLInputElement>} e */(e) => {
            const el = document.getElementById('currencyInput');
            // @ts-ignore TODO Get element correct type
            el.selectionStart = el.selectionEnd = 1000; // pushes caret to value end

            // @ts-ignore TODO Get event correct type
            if (e.nativeEvent.inputType === 'deleteContentBackward' && value && onChange) {
              return onChange(+((+((+value).toFixed(2).slice(0, -1)) / 10).toFixed(2)));
            }

            // @ts-ignore
            if (e.nativeEvent.inputType === 'insertText') {
              // @ts-ignore
              return onChange(+((value * 10 + +(e.nativeEvent.data) / 100).toFixed(2)) || value);
            }

            // @ts-ignore
            if (!e.nativeEvent.inputType && e.nativeEvent.code) { // is not a native event, can be from cypress
              // @ts-ignore
              if (e.nativeEvent.code === 'Delete') return 0;
              // @ts-ignore
              if (e.nativeEvent.code === 'Backspace' && onChange) {
                return onChange(+((+((+value).toFixed(2).slice(0, -1)) / 10).toFixed(2)));
              }
              // @ts-ignore
              if (!Number.isNaN(e.nativeEvent.code.slice(-1))) {
                // @ts-ignore
                return onChange(+((value * 10 + +(e.nativeEvent.code.slice(-1)) / 100).toFixed(2)) || value);
              }
            }

            if (onChange) return onChange(value || 0);
          }}
          disabled={disabled}
        />
      );
    }

    if (type === 'textarea') {
      return (
        <textarea {...inputProps} />
      );
    }

    if (type === 'phone' || type === 'email') {
      return (
        <div className="text-input">
          <Icon className={`${icon} align-left`} />

          <input
            className="padding-left"
            {...inputProps}
            name={type === 'email' ? 'email' : ''}
            type={type === 'phone' ? 'number' : 'text'}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && onKeyPress) onKeyPress();
              if (type === 'phone' && (e.key === '.' || e.key === ',' || e.key === '+' || e.key === 'e')) e.preventDefault();
            }}
          />
          {children}
        </div>
      );
    }

    if (type === 'document' || type === 'zipCode') {
      return (
        <div className="text-input">
          <input
            {...inputProps}
            type="text"
            onKeyPress={(e) => {
              if (!/^[0-9|.|\-|/\b]+$/.test(e.key)) e.preventDefault();
            }}
          />
          {children}
        </div>
      );
    }

    let classForTextNumber = iconBefore ? ' padding-left ' : '';
    classForTextNumber += iconAfter ? ' padding-right ' : '';

    return (
      <div className="text-input">
        {icon && iconBefore && <Icon className={`${iconBefore} align-left`} />}

        <input
          className={classForTextNumber}
          placeholder=""
          {...inputProps}
          type={type}
        />
        {children}
        {icon && iconAfter && <Icon className={`${iconAfter} align-right`} />}
      </div>
    );
  };

  render() {
    const {
      error, className, style, ...props
    } = this.props;

    return (
      <div className={`input-component ${className || ''} ${error ? 'error' : ''}`} style={style}>
        {this.renderInput(props)}

        {error && (
          <div className="error-warning" style={error && error.style && error.style}>
            <Icon className="circle exclamation" />
            <span>{typeof error !== 'string' && error.text ? error.text : error}</span>
          </div>
        )}

      </div>
    );
  }
}
